import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Mail, KeyRound } from "lucide-react";

const COUNTRY_CODES = ["nl","be","fr","de","uk","at","it","es","pt","lu","ch","se","dk","pl"];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { t, language, changeLanguage } = useI18n();
  const [profile, setProfile] = useState({
    name: user?.name || "", company_name: user?.company_name || "",
    vat_number: user?.vat_number || "", address: user?.address || "",
    phone: user?.phone || "", country: user?.country || ""
  });
  const [saving, setSaving] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [stripeEmail, setStripeEmail] = useState("");

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/profile", profile);
      updateUser(res.data);
      toast.success(t("toast.profile_updated"));
    } catch { toast.error(t("toast.save_failed")); }
    finally { setSaving(false); }
  };

  const requestPasswordReset = async () => {
    setSendingReset(true);
    try {
      await api.post("/auth/request-reset", { email: user?.email, frontend_url: window.location.origin });
      setResetSent(true);
      toast.success(t("toast.reset_sent"));
    } catch { toast.error(t("toast.send_failed")); }
    finally { setSendingReset(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
      <Tabs defaultValue="profile" className="w-full">
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">{t("settings.profile")}</TabsTrigger>
          <TabsTrigger value="password" data-testid="settings-tab-password">{t("settings.password")}</TabsTrigger>
          <TabsTrigger value="affiliate" data-testid="settings-tab-affiliate">{t("settings.affiliate_tab")}</TabsTrigger>
          <TabsTrigger value="language" data-testid="settings-tab-language">{t("settings.language")}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{t("settings.name_label")}</Label><Input value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} className="mt-1" data-testid="settings-name-input" /></div>
                <div><Label>{t("settings.company_label")}</Label><Input value={profile.company_name} onChange={(e) => setProfile({...profile, company_name: e.target.value})} className="mt-1" data-testid="settings-company-input" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{t("settings.vat_label")}</Label><Input value={profile.vat_number} onChange={(e) => setProfile({...profile, vat_number: e.target.value})} className="mt-1" data-testid="settings-vat-input" /></div>
                <div><Label>{t("settings.phone_label")}</Label><Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="mt-1" data-testid="settings-phone-input" /></div>
              </div>
              <div>
                <Label>{t("settings.country_label")}</Label>
                <Select value={profile.country || undefined} onValueChange={(v) => setProfile({...profile, country: v})}>
                  <SelectTrigger className="mt-1" data-testid="settings-country-select"><SelectValue placeholder={t("settings.country_placeholder")} /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((c) => <SelectItem key={c} value={c}>{t(`shops.country_${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">{t("settings.country_hint")}</p>
              </div>
              <div><Label>{t("settings.address_label")}</Label><Input value={profile.address} onChange={(e) => setProfile({...profile, address: e.target.value})} className="mt-1" data-testid="settings-address-input" /></div>
              <Button onClick={saveProfile} disabled={saving} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? t("settings.saving") : t("settings.save")}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="password">
          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  <KeyRound className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold">{t("settings.change_password")}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t("settings.change_password_desc")}</p>
                </div>
              </div>
              <div className="bg-accent/30 rounded-lg p-4">
                <p className="text-sm"><strong>{t("common.email")}</strong> <span className="font-mono-data">{user?.email}</span></p>
              </div>
              {resetSent ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t("settings.email_sent")}
                  </p>
                </div>
              ) : (
                <Button onClick={requestPasswordReset} disabled={sendingReset} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="request-reset-btn">
                  <Mail className="w-4 h-4 mr-2" />{sendingReset ? t("settings.sending") : t("settings.send_reset")}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="language">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <Label>{t("settings.language")}</Label>
              <Select value={language} onValueChange={changeLanguage}>
                <SelectTrigger className="mt-1 w-48" data-testid="language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nl">Nederlands</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiliate">
          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold">{t("settings.affiliate_payouts")}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t("settings.affiliate_desc")}</p>
              </div>
              <div>
                <Label>{t("settings.stripe_email")}</Label>
                <Input value={stripeEmail} onChange={(e) => setStripeEmail(e.target.value)} placeholder="je@stripe-email.com" className="mt-1" data-testid="stripe-email-input" />
                <p className="text-xs text-muted-foreground mt-1">{t("settings.stripe_hint")} <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-[#2CA8AC] underline">stripe.com</a>.</p>
              </div>
              <Button onClick={async () => {
                try {
                  await api.put("/affiliate/stripe-account", { stripe_account_id: stripeEmail });
                  toast.success(t("toast.saved"));
                } catch { toast.error(t("toast.generic_error")); }
              }} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="save-stripe-btn">
                <Save className="w-4 h-4 mr-2" />{t("common.save")}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
